// Agent-drafted plans and suggestions.
//
// POST /api/brands/:id/plans/draft quotes a draft, then confirms it.
// GET /api/brands/:id/plan-draft-runs lists runs, including resultingPlanId.
// GET /api/marketing-plans/:planId/suggestions?brandId= returns
// { draftSuggestions, metaWrites, modelCalls }. `suggestions` is not that
// list; plan detail uses it for coverage-gap builds.
// POST .../suggestions/:sid/accept?brandId= returns the new plan id.
// A duplicate accept is 409.
//
// A response status of flag-off or prod-refused is not the daily cap.
// overCap is true only when the server says the $5 cap was hit.
//
// Drafts render only when VITE_PLAN_DRAFTS=true.
// Suggestions render only when VITE_PLAN_SUGGESTIONS=true.
// Neither flag is set in this module.

import { apiJson, isApiError } from '../auth/apiFetch';
import { formatMoney, formatStamp, isMissingEndpoint, isSecretText, safeText } from './client';

/** Quote shown before a New plan draft runs. */
export type DraftQuote = {
  estimate: string | null;
  dailyCap: string | null;
  spentToday: string | null;
  /** True only when the server set overCap. Not used for flag-off. */
  overCap: boolean;
  /** Drafts are switched off. Distinct from the daily cap. */
  closed: 'flag-off' | 'prod-refused' | null;
  blockedReason: string | null;
  title: string | null;
  /** Confirm returned a plan id, so the draft was created. */
  created: boolean;
};

/** Scan or button run. */
export type DraftRunView = {
  summary: string;
  at: string | null;
  spend: string | null;
  resultingPlanId: string | null;
};

/** One draft suggestion from draftSuggestions. */
export type PlanSuggestionView = {
  id: string | null;
  kind: string | null;
  reason: string | null;
  effect: string | null;
  status: string | null;
  targetIds: string[];
};

export type AcceptSuggestionResult = {
  planId: string | null;
  duplicate: boolean;
  error: string | null;
};

type Rec = Record<string, unknown>;

export function planDraftsEnabled(env: { VITE_PLAN_DRAFTS?: string } = import.meta.env): boolean {
  return env.VITE_PLAN_DRAFTS === 'true';
}

export function originLabel(origin: string | null): string | null {
  if (origin === 'agent_scan') return 'Drafted by a scheduled scan';
  if (origin === 'agent_button') return 'Drafted from New plan';
  if (origin === 'chat') return 'Drafted in chat';
  return null;
}

export function draftUrl(brandId: string): string {
  return `/api/brands/${encodeURIComponent(brandId)}/plans/draft`;
}

export function draftRunsUrl(brandId: string): string {
  return `/api/brands/${encodeURIComponent(brandId)}/plan-draft-runs`;
}

export function suggestionsUrl(brandId: string, planId: string): string {
  return `/api/marketing-plans/${encodeURIComponent(planId)}/suggestions?brandId=${encodeURIComponent(brandId)}`;
}

export function acceptSuggestionUrl(brandId: string, planId: string, suggestionId: string): string {
  return `/api/marketing-plans/${encodeURIComponent(planId)}/suggestions/${encodeURIComponent(suggestionId)}/accept?brandId=${encodeURIComponent(brandId)}`;
}

export async function requestPlanDraft(
  brandId: string,
  confirm: boolean
): Promise<{ quote: DraftQuote | null; missing: boolean; created: boolean }> {
  try {
    const body = await apiJson<unknown>(draftUrl(brandId), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ confirm })
    });
    const quote = readDraftQuote(body, confirm);
    return { quote, missing: false, created: quote?.created === true };
  } catch (err: unknown) {
    if (isMissingEndpoint(err)) return { quote: null, missing: true, created: false };
    if (isApiError(err)) {
      const quote = readDraftQuote(err.body, confirm);
      if (quote?.closed || quote?.overCap) return { quote, missing: false, created: false };
    }
    return {
      quote: {
        estimate: null,
        dailyCap: null,
        spentToday: null,
        overCap: false,
        closed: null,
        blockedReason: "A new plan can't be drafted right now.",
        title: null,
        created: false
      },
      missing: false,
      created: false
    };
  }
}

export async function getDraftRuns(brandId: string): Promise<DraftRunView[]> {
  try {
    const body = await apiJson<unknown>(draftRunsUrl(brandId), { cache: 'no-store' });
    return readDraftRuns(body);
  } catch {
    return [];
  }
}

export async function getPlanSuggestions(brandId: string, planId: string): Promise<PlanSuggestionView[] | null> {
  try {
    const body = await apiJson<unknown>(suggestionsUrl(brandId, planId), { cache: 'no-store' });
    return readPlanSuggestions(body);
  } catch (err: unknown) {
    if (isMissingEndpoint(err)) return null;
    return null;
  }
}

export async function acceptPlanSuggestion(
  brandId: string,
  planId: string,
  suggestionId: string
): Promise<AcceptSuggestionResult> {
  try {
    const body = await apiJson<unknown>(acceptSuggestionUrl(brandId, planId, suggestionId), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    });
    return { planId: acceptedPlanId(body), duplicate: false, error: null };
  } catch (err: unknown) {
    if (isApiError(err) && err.status === 409) {
      return { planId: acceptedPlanId(err.body), duplicate: true, error: null };
    }
    if (isMissingEndpoint(err)) return { planId: null, duplicate: false, error: "Suggestions aren't available yet." };
    return { planId: null, duplicate: false, error: 'Could not accept this suggestion.' };
  }
}

export function readDraftQuote(body: unknown, confirm: boolean): DraftQuote | null {
  const payload = payloadOf(body);
  if (!payload) return null;
  const currency = currencyCode(payload.currency);
  const estimateUsd = num(payload.estimateUsd ?? nestedAmount(payload.estimate) ?? nestedAmount(payload.cost));
  const capUsd = num(payload.dailyCapUsd ?? nestedAmount(payload.dailyCap) ?? nestedAmount(payload.cap));
  const spentRaw = payload.spentTodayUsd ?? nestedAmount(payload.spentToday) ?? nestedAmount(payload.spent);
  const spentParsed = num(spentRaw);
  const spentKnown = payload.spentTodayUsd != null || payload.spentToday != null || payload.spent != null;
  const blocked = prose(payload.blockedReason, 240);
  const plan = isRec(payload.plan) ? payload.plan : null;
  const created = confirm && planIdOf(payload) != null;
  const closed = created ? null : closedStatus(payload.status);
  const overCap = !created && closed == null && payload.overCap === true;
  return {
    estimate: estimateUsd == null ? null : formatMoney(estimateUsd, currency),
    dailyCap: capUsd == null ? null : formatMoney(capUsd, currency),
    spentToday: !spentKnown || spentParsed == null ? null : formatMoney(spentParsed, currency),
    overCap,
    closed,
    blockedReason: created
      ? null
      : (closed === 'flag-off'
        ? "Plan drafts aren't turned on."
        : closed === 'prod-refused'
          ? "Plan drafts aren't available in this environment."
          : (blocked ?? (overCap ? 'This estimate is above the daily cap.' : null))),
    title: confirm && plan ? prose(plan.title ?? plan.name, 160) : null,
    created
  };
}

function closedStatus(value: unknown): 'flag-off' | 'prod-refused' | null {
  if (typeof value !== 'string') return null;
  const key = value.trim().toLowerCase();
  if (key === 'flag-off' || key === 'prod-refused') return key;
  return null;
}

function planIdOf(payload: Rec): string | null {
  const plan = isRec(payload.plan) ? payload.plan : null;
  const raw = payload.planId ?? plan?.planId ?? plan?.id;
  if (typeof raw === 'number' && Number.isFinite(raw)) return String(Math.trunc(raw));
  if (typeof raw !== 'string') return null;
  const text = raw.trim();
  if (!text || text.length > 80 || isSecretText(text)) return null;
  return text;
}

export function readDraftRuns(body: unknown): DraftRunView[] {
  const payload = payloadOf(body);
  if (!payload) return [];
  return records(payload.runs ?? payload.items ?? payload.planDraftRuns).map(readRun).filter((run): run is DraftRunView => run != null);
}

export function readPlanSuggestions(body: unknown): PlanSuggestionView[] | null {
  const payload = payloadOf(body);
  if (!payload || !('draftSuggestions' in payload)) return null;
  if (!Array.isArray(payload.draftSuggestions)) return null;
  return records(payload.draftSuggestions).map(readSuggestion).filter((row): row is PlanSuggestionView => row != null);
}

function acceptedPlanId(body: unknown): string | null {
  if (typeof body === 'string') return planIdText(body);
  if (!isRec(body)) return null;
  const plan = isRec(body.plan) ? body.plan : null;
  return planIdText(body.planId ?? body.newPlanId ?? plan?.planId ?? plan?.id ?? body.id);
}

function readRun(row: Rec): DraftRunView | null {
  const skipped = row.skipped === true || row.skip === true || prose(row.skipReason, 200) != null;
  const reason = prose(row.reason ?? row.skipReason ?? row.summary, 240);
  const title = prose(row.planTitle ?? row.title, 160);
  const summary = skipped
    ? `Skipped${reason ? `. ${reason}` : ''}`
    : [title, reason].filter(Boolean).join('. ');
  if (!summary) return null;
  const currency = currencyCode(row.currency);
  const spend = num(row.spend ?? row.actualUsd ?? row.estimatedUsd);
  return {
    summary,
    at: formatStamp(row.runAt ?? row.at ?? row.createdAt),
    spend: spend == null ? null : formatMoney(spend, currency),
    resultingPlanId: planIdText(row.resultingPlanId)
  };
}

function readSuggestion(row: Rec): PlanSuggestionView | null {
  const kind = kindLabel(row.kind ?? row.type);
  const reason = prose(row.reason ?? row.summary, 240);
  const effect = prose(row.expectedEffect ?? row.effect, 240);
  const status = statusLabel(row.status);
  const id = planIdText(row.id ?? row.suggestionId ?? row.sid);
  const targetIds = targetIdsOf(row.targetIds);
  if (!id && !kind && !reason && !effect) return null;
  return { id, kind, reason, effect, status, targetIds };
}

function targetIdsOf(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const ids: string[] = [];
  for (const item of value) {
    const text = planIdText(item);
    if (text) ids.push(text);
  }
  return ids;
}

function planIdText(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) return String(Math.trunc(value));
  if (typeof value !== 'string') return null;
  const text = value.trim();
  if (!text || text.length > 80 || isSecretText(text) || /^act_\d{4,}$/.test(text)) return null;
  return text;
}

function kindLabel(value: unknown): string | null {
  const raw = prose(value, 80);
  if (!raw || isIdToken(raw)) return null;
  const key = raw.toLowerCase().replace(/[_-]+/g, ' ');
  const labels: Record<string, string> = {
    restock: 'Colorway restock',
    'colorway restock': 'Colorway restock',
    creative: 'Winning creative type',
    seasonal: 'Seasonal peak',
    email: 'Email-driven demand'
  };
  return labels[key] ?? raw;
}

function statusLabel(value: unknown): string | null {
  const raw = prose(value, 40);
  if (!raw) return null;
  const key = raw.toLowerCase();
  if (key === 'open' || key === 'accepted' || key === 'dismissed') return key;
  return null;
}

function nestedAmount(value: unknown): unknown {
  if (!isRec(value)) return value;
  return value.usd ?? value.amount ?? value.value;
}

function payloadOf(body: unknown): Rec | null {
  return isRec(body) ? body : null;
}

function prose(value: unknown, limit: number): string | null {
  const raw = typeof value === 'string' ? value : safeText(value, limit);
  if (!raw) return null;
  const stripped = stripIds(raw);
  if (!stripped || isIdToken(stripped)) return null;
  const text = safeText(stripped, limit);
  if (!text || /[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}/.test(text)) return null;
  return text;
}

function stripIds(text: string): string {
  return text
    .replace(/\b[a-f0-9]{24}\b/gi, ' ')
    .replace(/\b[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/gi, ' ')
    .replace(/\bcreative_[A-Za-z0-9_-]+\b/gi, ' ')
    .replace(/EAA[A-Za-z0-9]{10,}/g, ' ')
    .replace(/\bact_\d{4,}/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function isIdToken(text: string): boolean {
  return /^[a-f0-9]{24}$/i.test(text)
    || /^act_\d{4,}$/.test(text)
    || /^EAA[A-Za-z0-9]{10,}$/.test(text)
    || /^creative_[A-Za-z0-9_-]+$/i.test(text)
    || /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(text);
}

function currencyCode(value: unknown): string | null {
  const text = typeof value === 'string' ? value.trim() : '';
  if (!/^[A-Za-z]{3}$/.test(text)) return null;
  return text.toUpperCase();
}

function num(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && /^-?\d+(\.\d+)?$/.test(value.trim())) {
    const parsed = Number(value.trim());
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function isRec(value: unknown): value is Rec {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

function records(value: unknown): Rec[] {
  if (!Array.isArray(value)) return [];
  return value.filter(isRec);
}
